import React, { useState, useRef } from 'react';
import { OverflowMenuVertical } from '@core/icons-react';
import { Tag, seg } from '../tags/Tag';
import { ActionDropdown } from '../dropdown/ActionDropdown';
import type { ActionCellProps } from './types';

/**
 * ActionCell - Renders overflow menu button with dropdown actions
 * 
 * Features:
 * - View and action options
 * - Configurable icon, size, position, direction
 * - Row-aware click handlers
 */
export const ActionCell: React.FC<ActionCellProps> = ({ 
  value, 
  config,
  row,
  rowIndex 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  
  if (!config) {
    return null;
  }
  
  const { 
    icon: Icon = OverflowMenuVertical,
    viewOptions = [],
    actionOptions = [],
    size = 'small',
    position = 'left',
    direction = 'down'
  } = config;
  
  // Process options to include row data and resolve disabled state
  const processedViewOptions = viewOptions.map(option => ({
    ...option,
    disabled: typeof option.disabled === 'function' 
      ? option.disabled(row, rowIndex) 
      : option.disabled,
    disabledReason: typeof option.disabledReason === 'function'
      ? option.disabledReason(row, rowIndex)
      : option.disabledReason,
    onClick: () => option.onClick(row, rowIndex)
  }));
  
  const processedActionOptions = actionOptions.map(option => ({
    ...option,
    disabled: typeof option.disabled === 'function' 
      ? option.disabled(row, rowIndex) 
      : option.disabled,
    disabledReason: typeof option.disabledReason === 'function'
      ? option.disabledReason(row, rowIndex)
      : option.disabledReason,
    onClick: () => option.onClick(row, rowIndex)
  }));
  
  return (
    <div ref={buttonRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, margin: 0 }}>
      <Tag
        template="action-sm"
        segments={[seg.icon(Icon, { size: 16 })]}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        style={{ padding: 0, margin: 0 }}
      />
      
      <ActionDropdown
        isOpen={isDropdownOpen}
        onClose={() => setIsDropdownOpen(false)}
        viewOptions={processedViewOptions}
        actionOptions={processedActionOptions}
        anchorRef={buttonRef}
        position={position}
        direction={direction}
        size={size}
        variant="default"
      />
    </div>
  );
};

